import React, { useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, Stack, Typography } from "@mui/material";

import { HashLink } from "react-router-hash-link";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AlertMessage from "../../../Common/Alert/AlertMessage";
import SegmentRender from "./SegmentRender";
import { checkSegmentStatus, formStatusMessage } from "../Appraisal/AppraisalFunctions";

const FormWrapper = ({
    container = "",
    title = "",
    subheader = "",
    helperText = "",
    data = {},
    canStart = false,
    canReview = false,
    canSubmit = false,
    onStart = null,
    onReview = null,
    onSubmit = null,
    onInfo = null,
    appraisalId = null,
    appraiseeUUID = null,
    isAppraiser = false,
    appCode,
    expandedSegment = null
}) => {
    const status = data?.status ?? "";
    const segments = data?.segments ?? [];
    const signOff = data?.signOff?.log?.length ? data.signOff : null;

    const messageStatus = canStart ? "not_started" : (!data?.id ? "no_data" : status);
    const alert = formStatusMessage(container, messageStatus, isAppraiser, helperText);

    const pendingSegments = segments.filter((segment) => ["warning", "error"].includes(checkSegmentStatus(segment, status, isAppraiser)));
    const pendingCount = pendingSegments?.length || 0;

    const [expandedSegments, setExpandedSegments] = useState(!!expandedSegment ? [expandedSegment] : []);
    const expandPendingSegments = () => {
        setExpandedSegments([...expandedSegments, ...pendingSegments.map(({index}) => `${container}-${index}`)]);
    };

    return (
        <Card className="card">
            <CardHeader
                disableTypography={true}
                title={
                    <Typography component="h2" className="card-header-title">
                        {title}
                    </Typography>
                }
                subheader={
                    <Typography component="span" className="card-header-subtitle" >
                        {subheader}
                    </Typography>
                }
                action={
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                        sx={{ padding: "8px" }}
                    >
                        {canStart && (
                            <Button
                                variant="contained"
                                color="primary"
                                disableElevation={true}
                                aria-label="Start"
                                onClick={onStart}
                                endIcon={<SendIcon />}
                            >
                                Start
                            </Button>
                        )}

                        {pendingCount > 0 && (
                            <HashLink
                                to={`/rms/appraisals/${appraisalId}#io-segment-${container}-${pendingSegments[0].index}`}
                                scroll={(el) => {
                                    const yCoordinate = el.getBoundingClientRect().top + window.scrollY;
                                    const yOffset = -80;
                                    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="warning"
                                    disableElevation
                                    aria-label={`See pending ${container}`}
                                    onClick={expandPendingSegments}
                                    startIcon={<WarningAmberOutlinedIcon />}
                                    sx={{ color: "#FFF" }}
                                >
                                    {pendingCount}
                                </Button>
                            </HashLink>
                        )}

                        {signOff && (
                            <Button
                                variant="contained"
                                color="neutral"
                                disableElevation={true}
                                aria-label="Info"
                                onClick={onInfo}
                                startIcon={<InfoOutlinedIcon />}
                            >
                                Info
                            </Button>
                        )}

                        {canSubmit && !pendingCount && (
                            <Button
                                variant="contained"
                                color="primary"
                                disableElevation={true}
                                aria-label="Submit for approval"
                                onClick={onSubmit}
                                endIcon={<SendIcon />}
                            >
                                Submit
                            </Button>
                        )}

                        {canReview && (
                            <Button
                                variant="contained"
                                color="primary"
                                disableElevation={true}
                                aria-label="Review"
                                endIcon={<FileDownloadDoneIcon />}
                                onClick={onReview}
                            >
                                Review
                            </Button>
                        )}
                    </Stack>
                }
            />

            {alert?.message && (
                <CardContent>
                    <Box className="card-content">
                        <AlertMessage
                            severity={alert.severity}
                            message={alert.message}
                        />
                    </Box>
                </CardContent>
            )}

            {(data?.id || isAppraiser) && (
                <CardContent>
                    <Box className="card-content">
                        {segments.length > 0 ? (
                            segments
                                .sort((a, b) => a.index - b.index)
                                .map((segment) => (
                                    <SegmentRender
                                        segment={segment}
                                        key={segment.index}
                                        appraisalId={appraisalId}
                                        appraiseeUUID={appraiseeUUID}
                                        isAppraiser={isAppraiser}
                                        segmentStatus={checkSegmentStatus(segment, status, isAppraiser)}
                                        container={container}
                                        appCode={appCode}
                                        expanded={expandedSegments.includes(`${container}-${segment.index}`)}
                                        onExpandOrCollapse={(expanded) => {
                                            const segmentId = `${container}-${segment.index}`;
                                            if (!expanded) {
                                                setExpandedSegments(expandedSegments.filter((seg) => seg !== segmentId));
                                            } else if (!expandedSegments.includes(segmentId)) {
                                                setExpandedSegments([...expandedSegments, segmentId]);
                                            }
                                        }}
                                    />
                                ))
                        ) : (
                            <AlertMessage
                                severity="info"
                                message="No data available"
                            />
                        )}
                    </Box>
                </CardContent>
            )}
        </Card>
    );
};

export default FormWrapper;
